import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Row,
  Col,
  message,
  DatePicker,
  Tabs,
  Table,
  Typography,
  Alert,
  Checkbox,
} from 'antd';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import { MinusCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import * as api from 'app/shared/api';
import {
  PackageItemType,
  PackageProduct,
  PackageProductItem,
} from 'app/shared/model/Package';
import { Product } from 'app/shared/model/ProductBase';
import {
  AccountCategory,
  CommissionCategory,
  ProductCategory,
  BookingSystem,
  Fee,
} from 'app/shared/model';
import {
  getPackageById,
  getAccountCategories,
  getCommissionCategories,
  getProductCategories,
} from '../selectors';
import {
  productTypeIdToKey,
  productTypeKeyToId,
} from 'app/shared/api/mappers/mapPackageProduct';

const { TextArea } = Input;
const { Text } = Typography;

interface PackageFormOwnProps {
  packageId?: number;
  onSuccess?: (updatedPackages?: PackageProduct[]) => void | Promise<void>;
}

type PackageItemFormValue = {
  productSelection?: string;
  percentageBreakdown?: number;
};

type OptionsByType = Record<PackageItemType, { label: string; value: string }[]>;

type ItemCounts = Record<PackageItemType, number>;

const optionTypes: PackageItemType[] = [
  'Course',
  'Accommodation',
  'Transfer',
  'AddOn',
  'Fees',
];

const tabItems = [
  { key: 'Course', label: 'Course' },
  { key: 'Accommodation', label: 'Accommodation' },
  { key: 'Transfer', label: 'Transfer' },
  { key: 'AddOn', label: 'Add-ons' },
  { key: 'Fees', label: 'Fees' },
];

const allowedDifference = 0.05;

import { getDivisionId } from 'app/shared/division/getDivisionId';
const PACKAGE_PRODUCT_TYPE_ID = 5;
const initialOptions: OptionsByType = {
  Course: [],
  Accommodation: [],
  Transfer: [],
  AddOn: [],
  Fees: [],
};

const countItems = (items: PackageProductItem[]): ItemCounts => {
  return items.reduce<ItemCounts>(
    (acc, item) => {
      if (item.productType) {
        acc[item.productType] = (acc[item.productType] || 0) + 1;
      }
      return acc;
    },
    { Course: 0, Accommodation: 0, Transfer: 0, AddOn: 0, Fees: 0 }
  );
};

const distributePercentages = (items: PackageProductItem[]): PackageProductItem[] => {
  const count = items.length;
  if (!count) return items;
  const even = Number((100 / count).toFixed(1));
  let remaining = 100;

  return items.map((item, index) => {
    const value =
      index === count - 1
        ? Number(remaining.toFixed(1))
        : Number(Math.min(remaining, even).toFixed(1));
    remaining = Number((remaining - value).toFixed(1));
    return {
      ...item,
      percentageBreakdown: value,
    };
  });
};

const withinOneDecimal = (value: number) =>
  Math.abs(value * 10 - Math.round(value * 10)) < 0.0001;

const AddEditPackageForm: React.FC<PackageFormOwnProps> = ({
  packageId,
  onSuccess,
}) => {
  const packageProduct = useSelector(getPackageById);
  const productCategories = useSelector(getProductCategories) ?? [];
  const accountCategories = useSelector(getAccountCategories) ?? [];

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsByType, setOptionsByType] = useState<OptionsByType>(initialOptions);
  const [items, setItems] = useState<PackageProductItem[]>([]);
  const [selectedTab, setSelectedTab] = useState<PackageItemType>('Course');
  const optionNameMapRef = useRef<Map<string, string>>(new Map());
  const divisionId = packageProduct?.division ?? getDivisionId();
  const [bookingSystems, setBookingSystems] = useState<BookingSystem[]>([]);
  const [isExclusionLoading, setIsExclusionLoading] = useState(false);
  const [initialExcludedSystems, setInitialExcludedSystems] = useState<string[]>([]);
  const nameValue = Form.useWatch('name', form) ?? '';
  const minimumAgeValue = Form.useWatch('minimumAge', form);
  const maximumAgeValue = Form.useWatch('maximumAge', form);

  const minimumWeeksValue = Form.useWatch('minimumWeeks', form);

  const minWeeksValid =
      minimumWeeksValue === undefined ||
      minimumWeeksValue === null ||
      (Number.isInteger(minimumWeeksValue) &&
          minimumWeeksValue >= 0 &&
          minimumWeeksValue <= 999);

  const productCategoryOptions = useMemo(
    () =>
      productCategories.map((category) => ({
        label: category.name,
        value: Number(category.id),
      })),
    [productCategories]
  );

  const accountCategoryOptions = useMemo(
    () =>
      accountCategories.map((category) => ({
        label: category.name,
        value: Number(category.id),
      })),
    [accountCategories]
  );

  const counts = useMemo(() => countItems(items), [items]);

  const totalPercentage = useMemo(() => {
    const sum = items.reduce(
      (acc, item) => acc + (Number(item.percentageBreakdown) || 0),
      0
    );
    return Number(sum.toFixed(1));
  }, [items]);

  const divisionMissing = !divisionId;

  const isPercentageValid =
    items.length === 0 || Math.abs(totalPercentage - 100) <= allowedDifference;

  const nameTrimmed = nameValue.trim();
  const nameValid =
    nameTrimmed.length >= 1 &&
    nameTrimmed.length <= 255;

  const minAgeValid =
    minimumAgeValue === undefined ||
    minimumAgeValue === null ||
    (Number.isInteger(minimumAgeValue) &&
      minimumAgeValue >= 0 &&
      minimumAgeValue <= 99);

  const maxAgeValid =
    maximumAgeValue === undefined ||
    maximumAgeValue === null ||
    (Number.isInteger(maximumAgeValue) &&
      maximumAgeValue >= 0 &&
      maximumAgeValue <= 99);

  const ageOrderValid =
    minimumAgeValue === undefined ||
    minimumAgeValue === null ||
    maximumAgeValue === undefined ||
    maximumAgeValue === null ||
    maximumAgeValue >= minimumAgeValue;

  const hasItems = items.length > 0;
  const itemRulesSatisfied =
    counts.Course <= 1 && counts.Accommodation <= 1 && counts.Transfer <= 2;

  const canSave =
    nameValid &&
    minAgeValid &&
    maxAgeValid &&
      minWeeksValid &&
    ageOrderValid &&
    hasItems &&
    itemRulesSatisfied &&
    isPercentageValid &&
    !divisionMissing;

  const availableOptions = useMemo(() => {
    if (divisionMissing) {
      return [];
    }
    const currentOptions = optionsByType[selectedTab] ?? [];
    return currentOptions.filter(
      (option) =>
        !items.some(
          (item) =>
            `${item.productType}|${item.productId}` === option.value
        )
    );
  }, [items, optionsByType, selectedTab, divisionMissing]);

  const typeLimitReached = useMemo(() => {
    if (divisionMissing) {
      return false;
    }
    switch (selectedTab) {
      case 'Course':
        return counts.Course >= 1;
      case 'Accommodation':
        return counts.Accommodation >= 1;
      case 'Transfer':
        return counts.Transfer >= 2;
      default:
        return false;
    }
  }, [counts, selectedTab, divisionMissing]);

  const updateOptionNameMap = (options: OptionsByType) => {
    optionTypes.forEach((type) => {
      options[type].forEach((option) => {
        optionNameMapRef.current.set(option.value, option.label);
      });
    });
  };

  const loadProductOptions = useCallback(async (activeDivisionId?: number) => {
    if (
      activeDivisionId === undefined ||
      activeDivisionId === null ||
      Number.isNaN(Number(activeDivisionId))
    ) {
      setOptionsByType(initialOptions);
      return;
    }

    setLoadingOptions(true);
    try {
      const [courses, accommodations, addons, transfers, fees] = await Promise.all([
        api.getCourseProducts(activeDivisionId),
        api.getAccommodationRoomProductsByDivision(activeDivisionId),
        api.getAddonsProducts(activeDivisionId),
        api.getTransferProducts(activeDivisionId),
        api.getAllFeesForDivision(activeDivisionId),
      ]);

      const targetDivisionId = Number(activeDivisionId);
      const hasTargetDivision = !Number.isNaN(targetDivisionId);
      const normaliseDivision = (value: unknown) => {
        if (value === null || value === undefined) {
          return undefined;
        }
        const numeric = Number(value);
        return Number.isNaN(numeric) ? undefined : numeric;
      };

      const filterByDivision = (item: Product | Fee) => {
        if (!hasTargetDivision) {
          return true;
        }
        if (!item) {
          return false;
        }
        const itemDivision =
          normaliseDivision((item as any).division) ??
          normaliseDivision((item as any).divisionId);
        if (itemDivision === undefined) {
          return true;
        }
        return itemDivision === targetDivisionId;
      };

      const filterByActive = (item: Product | Fee) => {
        return (item as any)?.active !== false;
      };

      const nextOptions: OptionsByType = {
        Course: (courses ?? []).filter(filterByDivision).filter(filterByActive).map((item) => ({
          label: item.name,
          value: `Course|${item.id}`,
        })),
        Accommodation: (accommodations ?? [])
          .filter((item): item is NonNullable<typeof item> => Boolean(item) && filterByDivision(item) && filterByActive(item))
          .map((item) => ({
            label: item.name,
            value: `Accommodation|${item.id}`,
          })),
        AddOn: (addons ?? []).filter(filterByDivision).filter(filterByActive).map((item) => ({
          label: item.name,
          value: `AddOn|${item.id}`,
        })),
        Transfer: (transfers ?? []).filter(filterByDivision).filter(filterByActive).map((item) => ({
          label: item.name,
          value: `Transfer|${item.id}`,
        })),
        Fees: (fees ?? []).filter(filterByActive).map((item) => ({
          label: item.name,
          value: `Fees|${item.id}`,
        })),
      };

      updateOptionNameMap(nextOptions);
      setOptionsByType(nextOptions);
    } catch (error) {
      console.error('Failed to load selectable products for packages', error);
      message.error('Failed to load products. Please refresh and try again.');
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    api.getAllBookingSystems()
      .then((systems) => {
        if (isMounted) setBookingSystems(systems);
      })
      .catch((err: any) => {
        message.error(err?.message ?? 'Failed to load booking systems');
      });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const productId = packageProduct?.id ?? 0;
    if (!productId) {
      setInitialExcludedSystems([]);
      form.setFieldsValue({ excludedBookingSystemIds: [] });
      setIsExclusionLoading(false);
      return;
    }

    let cancelled = false;
    setIsExclusionLoading(true);
    api.getBookingSystemProductExclusions()
      .then((exclusions) => {
        if (cancelled) return;
        const selected = exclusions
          .filter(
            (summary) =>
              Array.isArray(summary.productsExcluded) &&
              summary.productsExcluded.some(
                (productExclusion) =>
                  Number(productExclusion.productId) === Number(productId) &&
                  Number(productExclusion.productTypeId) === PACKAGE_PRODUCT_TYPE_ID
              )
          )
          .map((summary) => String(summary.bookingSystemId));
        setInitialExcludedSystems(selected);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setInitialExcludedSystems([]);
          message.error(err?.message ?? 'Failed to load booking system exclusions');
        }
      })
      .finally(() => {
        if (!cancelled) setIsExclusionLoading(false);
      });

    return () => { cancelled = true; };
  }, [packageProduct?.id, form]);

  useEffect(() => {
    form.setFieldsValue({ excludedBookingSystemIds: initialExcludedSystems });
  }, [initialExcludedSystems, form]);

  useEffect(() => {
    if (!divisionId) {
      setOptionsByType(initialOptions);
      setItems([]);
      return;
    }

    loadProductOptions(divisionId);
  }, [divisionId, loadProductOptions]);

  useEffect(() => {
    setItems((prev) =>
      prev.map((item) => {
        const key = `${item.productType}|${item.productId}`;
        const label = optionNameMapRef.current.get(key);
        if (label && label !== item.productName) {
          return { ...item, productName: label };
        }
        return item;
      })
    );
  }, [optionsByType]);

  const applyInitialState = useCallback(
    (currentPackage?: PackageProduct) => {
      const initialItems: PackageProductItem[] = (currentPackage?.items ?? []).map(
        (item) => {
          const type =
            item.productType ??
            productTypeIdToKey[item.productTypeId] ??
            'Course';
          const key = `${type}|${item.productId}`;
          const label =
            optionNameMapRef.current.get(key) ||
            item.productName ||
            `${type} #${item.productId}`;
          optionNameMapRef.current.set(key, label);

          return {
            productId: item.productId,
            productType: type,
            productTypeId: item.productTypeId ?? productTypeKeyToId[type],
            productName: label,
            percentageBreakdown: Number(
              (item.percentageBreakdown ?? 0).toFixed(1)
            ),
          };
        }
      );

      setItems(initialItems);
      setSelectedTab('Course');

      form.setFieldsValue({
        id: currentPackage?.id ?? undefined,
        name: currentPackage?.name ?? undefined,
        description: currentPackage?.description ?? undefined,
        minimumAge: currentPackage?.minimumAge ?? undefined,
        maximumAge: currentPackage?.maximumAge ?? undefined,
        minimumWeeks: currentPackage?.minimumWeeks ?? undefined,
        accountCategoryId: currentPackage?.accountCategoryId ?? undefined,
        productCategoryId: currentPackage?.productCategoryId ?? undefined,
        active: currentPackage?.active ?? true,
      });
    },
    [form]
  );

  useEffect(() => {
    const current =
      packageId && packageProduct && packageProduct.id === packageId
        ? packageProduct
        : undefined;
    applyInitialState(current);
  }, [applyInitialState, packageId, packageProduct]);

  const handleAddItem = (value?: string) => {
    if (!divisionId) {
      message.warning('Please select a division in the header before adding items.');
      return;
    }
    if (!value) {
      return;
    }
    const [typeKey, idValue] = value.split('|');
    const productType = typeKey as PackageItemType;
    const productId = Number(idValue);

    if (Number.isNaN(productId) || !productType) {
      return;
    }

    if (
      items.some(
        (item) =>
          item.productId === productId && item.productType === productType
      )
    ) {
      message.warning('This product is already included in the package.');
      return;
    }

    if (productType === 'Course' && counts.Course >= 1) {
      message.warning('Only one course can be added to a package.');
      return;
    }

    if (productType === 'Accommodation' && counts.Accommodation >= 1) {
      message.warning('Only one accommodation can be added to a package.');
      return;
    }

    if (productType === 'Transfer' && counts.Transfer >= 2) {
      message.warning('You can add up to two transfers to a package.');
      return;
    }

    const productName =
      optionNameMapRef.current.get(value) ||
      `${productType} #${productId}`;

    const updatedItems = distributePercentages([
      ...items,
      {
        productId,
        productType,
        productTypeId: productTypeKeyToId[productType],
        productName,
        percentageBreakdown: 0,
      },
    ]);

    setItems(updatedItems);
  };

  const handleRemoveItem = (productId: number, productType: PackageItemType) => {
    setItems((prev) =>
      prev.filter(
        (item) =>
          !(
            item.productId === productId && item.productType === productType
          )
      )
    );
  };

  const handlePercentageChange = (
    productId: number,
    productType: PackageItemType,
    value: number | null
  ) => {
    if (value === null || value === undefined) {
      return;
    }
    const nextValue = Number(value.toFixed(1));
    setItems((prev) =>
      prev.map((item) =>
        item.productId === productId && item.productType === productType
          ? { ...item, percentageBreakdown: nextValue }
          : item
      )
    );
  };

  const columns: ColumnsType<PackageProductItem> = [
    {
      title: 'Product Type',
      dataIndex: 'productType',
      key: 'productType',
      render: (type: PackageItemType | undefined) => {
        if (!type) return '';
        return type === 'AddOn' ? 'Add-on' : type;
      },
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      render: (text: string) => text || '-',
    },
    {
      title: 'Price breakdown %',
      dataIndex: 'percentageBreakdown',
      key: 'percentageBreakdown',
      width: 160,
      render: (_: number, record: PackageProductItem) => (
        <InputNumber
          min={0}
          max={100}
          precision={1}
          value={record.percentageBreakdown}
          onChange={(value) =>
            handlePercentageChange(
              record.productId,
              record.productType || 'Course',
              value
            )
          }
          status={isPercentageValid ? undefined : 'error'}
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: unknown, record: PackageProductItem) => (
        <Button
          type="link"
          onClick={() =>
            handleRemoveItem(
              record.productId,
              record.productType || 'Course'
            )
          }
          icon={<MinusCircleOutlined />}
        >
          Remove
        </Button>
      ),
    },
  ];

  const handleFinish = async (values: any) => {
    if (!divisionId) {
      message.error('Please select a division before saving a package.');
      return;
    }

    if (!items.length) {
      message.error('Please add at least one product to the package.');
      return;
    }

    const trimmedName = (values.name ?? '').trim();
    form.setFieldsValue({ name: trimmedName });

    const payload: PackageProduct = {
      id: values.id ?? 0,
      name: trimmedName,
      description: (values.description ?? '').trim(),
      minimumAge: values.minimumAge ?? null,
      maximumAge: values.maximumAge ?? null,
      minimumWeeks: values.minimumWeeks ?? null,
      accountCategoryId: values.accountCategoryId ?? null,
      productCategoryId: values.productCategoryId ?? null,
      items: items.map((item) => ({
        productId: item.productId,
        productType: item.productType,
        productTypeId: item.productTypeId,
        productName: item.productName,
        percentageBreakdown: Number(item.percentageBreakdown.toFixed(1)),
      })),
      division: divisionId,
      divisionText: packageProduct?.divisionText,
      entityUpdate: packageProduct?.entityUpdate ?? false,
      active: values.active ?? true,
    };

    setSaving(true);
    try {
      const response = await api.savePackage(payload, divisionId);
      if (!response.ok) {
        const messageText = response.message || 'Failed to save package.';
        if (messageText.toLowerCase().includes('exists')) {
          form.setFields([
            {
              name: 'name',
              errors: ['A package with this name already exists'],
            },
          ]);
        } else {
          message.error(messageText);
        }
        return;
      }

      message.success(response.message || 'Package saved successfully.');

      const savedPackageIdRaw =
        response.productId ?? response.id ?? payload.id ?? 0;
      const savedPackageId = Number(savedPackageIdRaw);
      const selectedSystems = form.getFieldValue('excludedBookingSystemIds') as (string | number)[] | undefined;

      if (savedPackageId > 0) {
        try {
          const updatedIds = await api.updateBookingSystemProductExclusions(
            savedPackageId,
            PACKAGE_PRODUCT_TYPE_ID,
            selectedSystems,
            initialExcludedSystems
          );
          setInitialExcludedSystems(updatedIds);
        } catch (err: any) {
          message.error(err?.message ?? 'Failed to update booking system exclusions.');
          return;
        }
      } else if (Array.isArray(selectedSystems) && selectedSystems.length > 0) {
        message.warning('Saved, but booking system exclusions could not be updated because the package id was unavailable.');
      }

      let latestPackages: PackageProduct[] | undefined;
      try {
        latestPackages = await api.getPackageProducts(divisionId);
      } catch (reloadError) {
        console.error('Failed to reload packages list', reloadError);
      }

      if (onSuccess) {
        await onSuccess(latestPackages);
      }
      applyInitialState(undefined);
    } catch (error) {
      console.error('Failed to save package', error);
      message.error('An unexpected error occurred while saving the package.');
    } finally {
      setSaving(false);
    }
  };

  const formDisabled = Boolean(packageProduct?.entityUpdate);

  return (
    <Form
      layout="vertical"
      form={form}
      disabled={formDisabled}
      onFinish={handleFinish}
    >
      {formDisabled && (
        <Alert type="info" title="Publishing an update" description="This record is locked while an update is being published. Editing is disabled." showIcon style={{ marginBottom: 16 }} />
      )}
      <Form.Item name="id" hidden>
        <Input type="hidden" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={
              <span>
                 Package name
              </span>
            }
            name="name"
            rules={[
              {
                required: true,
                message: 'Please enter a package name.',
                transform: (value) => (value ?? '').trim(),
              },
              {
                validator: (_, value) => {
                  const trimmed = (value ?? '').trim();
                  if (trimmed.length > 100) {
                    return Promise.reject(
                      new Error('Package name must be between 1 and 100 characters.')
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter package name" maxLength={100} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Description" name="description">
            <TextArea rows={5} placeholder="Describe this package" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Minimum age"
                name="minimumAge"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === null || value === undefined) {
                        return Promise.resolve();
                      }
                      if (
                        !Number.isInteger(value) ||
                        value < 0 ||
                        value > 99
                      ) {
                        return Promise.reject(
                          new Error('Minimum age must be between 0 and 99.')
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber min={0} max={99} style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Maximum age"
                name="maximumAge"
                dependencies={['minimumAge']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === null || value === undefined) {
                        return Promise.resolve();
                      }
                      if (
                        !Number.isInteger(value) ||
                        value < 0 ||
                        value > 99
                      ) {
                        return Promise.reject(
                          new Error('Maximum age must be between 0 and 99.')
                        );
                      }
                      const minValue = form.getFieldValue('minimumAge');
                      if (
                        minValue !== null &&
                        minValue !== undefined &&
                        value < minValue
                      ) {
                        return Promise.reject(
                          new Error('Maximum age must be greater than minimum age.')
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber min={0} max={99} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                  label="Minimum weeks"
                  name="minimumWeeks"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === null || value === undefined) return Promise.resolve();
                        if (!Number.isInteger(value) || value < 0 || value > 999) {
                          return Promise.reject(new Error('Minimum weeks must be between 0 and 999.'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
              >
                <InputNumber
                    min={0}
                    max={999}
                    precision={0}
                    style={{ width: '100%' }}
                    placeholder="Enter minimum weeks"
                />
              </Form.Item>
            </Col>
            <Col span={12} />
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                  label="Product category"
                  name="productCategoryId"
                  rules={[{ required: true, message: 'Please select a product category' }]}
              >
                <Select
                    allowClear
                    options={productCategoryOptions}
                    placeholder="Select product category"
                    showSearch={{ optionFilterProp: 'label' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                  label="Account category"
                  name="accountCategoryId"
                  rules={[{ required: true, message: 'Please select an account category' }]}
              >
                <Select
                    allowClear
                    options={accountCategoryOptions}
                    placeholder="Select account category"
                    showSearch={{ optionFilterProp: 'label' }}
                />
              </Form.Item>
            </Col>
          </Row>

        </Col>
      </Row>

      <Tabs
        items={tabItems}
        activeKey={selectedTab}
        onChange={(key) => setSelectedTab(key as PackageItemType)}
        style={{ marginBottom: 12 }}
      />

      <Select
        key={selectedTab}
        style={{ width: '100%', marginBottom: 16 }}
        placeholder={
          divisionMissing
            ? 'Select a division in the header to load products'
            : 'Select or type product'
        }
        showSearch={{
          filterOption: (input, option) =>
            (option?.label as string)
              ?.toLowerCase()
              .includes(input.toLowerCase()),
        }}
        value={undefined}
        options={availableOptions}
        loading={loadingOptions}
        onChange={(value) => {
          if (value) {
            handleAddItem(value);
          }
        }}
        disabled={
          formDisabled ||
          divisionMissing ||
          typeLimitReached ||
          loadingOptions ||
          !availableOptions.length
        }
      />

      {divisionMissing && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Select a division in the header to load products.
        </Text>
      )}

      <Table
        columns={columns}
        dataSource={items}
        rowKey={(record) => `${record.productType}-${record.productId}`}
        pagination={false}
        locale={{
          emptyText: 'Add products to build this package.',
        }}
        style={{ marginBottom: 8 }}
      />

      {!isPercentageValid && (
        <Text type="danger">Price breakdown must total 100%</Text>
      )}

      <Row style={{ marginTop: 16 }}>
        <Col span={24}>
          <Form.Item
            name="excludedBookingSystemIds"
            label={
              <>
                <span style={{ fontWeight: 'bold', color: 'red', marginRight: 3 }}>Exclude</span>
                {' this package from the following booking systems:'}
              </>
            }
          >
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              disabled={isExclusionLoading}
            >
              {bookingSystems.map((system) => (
                <Checkbox key={system.id} value={String(system.id)}>
                  {system.name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row style={{ marginTop: 24 }} justify="space-between" align="middle">
        <Col>
          <ActiveStatusFormItem name="active" initialValue />
        </Col>
        <Col>
          <Space>
            <Button onClick={() => applyInitialState(packageProduct)}>
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => form.submit()}
              disabled={!canSave || formDisabled}
              loading={saving}
            >
              Save
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export default AddEditPackageForm;